import { useMutation } from '@apollo/client';
import type { UseUploadSegment } from '@openx/components-segment/lib/utils/context/SegmentHookTypes';
import {
  type CreateSegment,
  defaultSegmentCategory,
  defaultSegmentProvider,
  defaultSegmentSubCategory,
} from '@openx/types/segments';
import { enqueueSnackbar } from 'notistack';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../components/AuthContext/context';
import { AddUsersToSegmentDocument } from '../../../graphql/mutation/segments/AddUsersToSegment.generated';
import { CreateSegmentDocument } from '../../../graphql/mutation/segments/CreateSegment.generated';
import { UpdateSegmentDocument } from '../../../graphql/mutation/segments/UpdateSegment.generated';
import { NestedErrorCode } from '../../../modules/graphql/types';
import { useLocalErrorHandling } from '../../../modules/graphql/useLocalErrorHandling';
import { createFullName } from '../../../modules/segments/utils/createFullName';

export const useUploadSegment: UseUploadSegment = () => {
  const { t } = useTranslation();
  const { session } = useAuth();

  const [createSegment] = useMutation(CreateSegmentDocument);
  const [updateSegment] = useMutation(UpdateSegmentDocument);
  const [addUsers] = useMutation(AddUsersToSegmentDocument, {
    fetchPolicy: 'no-cache',
  });

  const addUsersToSegment = useCallback(
    (id: string, data: string[][], headers: string[]) =>
      addUsers({
        variables: {
          data: {
            data,
            headers,
          },
          segmentId: id,
        },
      }),
    [addUsers]
  );

  const setStatusToPublishing = useCallback(
    (id: string) =>
      updateSegment({
        variables: {
          set: {
            status: 'publishing',
          },
          where: {
            id: { _eq: id },
          },
        },
      }),
    [updateSegment]
  );

  const uploadSegment = useCallback(
    async (name: string): Promise<CreateSegment | undefined> => {
      const createSegmentResponse = await createSegment({
        refetchQueries: ['GetSegmentsList', 'GetSegmentFeed'],
        variables: {
          data: {
            account_id: session?.account.id,
            category: defaultSegmentCategory,
            description: '',
            full_name: createFullName(name),
            is_leaf: true,
            name: name,
            path: [defaultSegmentCategory, defaultSegmentSubCategory, name],
            provider_id: defaultSegmentProvider,
            source: '',
            status: 'error',
            sub_category: defaultSegmentSubCategory,
            sub_type: 'uploaded',
            type: '1p',
          },
        },
      });

      return createSegmentResponse.data?.segment?.returning[0];
    },
    [createSegment, session?.account.id]
  );

  const errorCallback = useCallback(
    error => {
      if (error.code !== NestedErrorCode.JOINT_UNIQUENESS_VIOLATION || error.path !== 'objects.name') return false;

      enqueueSnackbar(t('Segment with such name already exists'), { variant: 'error' });
      return true;
    },
    [t]
  );
  useLocalErrorHandling(CreateSegmentDocument, errorCallback);

  return {
    addUsersToSegment,
    setStatusToPublishing,
    uploadSegment,
  };
};
